// src/pages/Reportes.js
//
// Reportes conectados a datos reales del backend heredado, con exportación
// funcional a PDF (jspdf + jspdf-autotable) y Excel (xlsx / SheetJS).
//
// Los 6 reportes disponibles:
//   1. Ventas por período       — serie diaria + IVA + utilidad
//   2. Productos más vendidos    — top ranking por facturación
//   3. Estado del inventario     — stock, costo, valorización
//   4. Clientes frecuentes       — top clientes por facturación
//   5. Utilidad y márgenes       — rentabilidad por categoría
//   6. Domicilios                — pedidos HolaVeci por estado

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Stack, Button,
  ToggleButtonGroup, ToggleButton, CircularProgress, Alert, LinearProgress,
  Chip,
} from '@mui/material';
import PictureAsPdfIcon  from '@mui/icons-material/PictureAsPdf';
import TableChartIcon    from '@mui/icons-material/TableChart';
import AnalyticsIcon     from '@mui/icons-material/Analytics';
import Inventory2Icon    from '@mui/icons-material/Inventory2';
import PeopleIcon        from '@mui/icons-material/People';
import PaidIcon          from '@mui/icons-material/Paid';
import ReceiptLongIcon   from '@mui/icons-material/ReceiptLong';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, Legend,
} from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { useApp } from '../context/AppContext';
import { reportes as apiReportes } from '../api/posApi';

const fmtCOP = (n) => (Number(n) || 0).toLocaleString('es-CO',
  { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
const fmtNum = (n) => (Number(n) || 0).toLocaleString('es-CO');
const fmtFecha = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString('es-CO'); } catch { return s; }
};
const isoDia = (offset = 0) =>
  new Date(Date.now() + offset * 86400000).toISOString();

const Reportes = () => {
  const { empresa, sucursalActiva, sucursalActivaId, notify } = useApp();
  const empresaId = empresa?.empresaId || empresa?.EmpresaId;
  const nombreEmpresa = empresa?.nombreComercial || empresa?.razonSocial
                     || empresa?.NombreComercial || empresa?.RazonSocial
                     || 'Empresa';
  const nombreSucursal = sucursalActiva?.nombre || 'Todas las sucursales';

  const [periodo, setPeriodo] = useState('30');
  const [serie, setSerie] = useState([]);
  const [cargandoSerie, setCargandoSerie] = useState(false);
  const [exportando, setExportando] = useState(null);

  const diasNum = parseInt(periodo, 10);
  const desde = useMemo(() => isoDia(-(diasNum - 1)), [diasNum]);
  const hasta = useMemo(() => isoDia(1), [diasNum]);

  const cargarSerie = useCallback(async () => {
    if (!empresaId) return;
    setCargandoSerie(true);
    try {
      const r = await apiReportes.serieDiaria(empresaId, sucursalActivaId, diasNum);
      setSerie(Array.isArray(r) ? r : []);
    } catch { /* silent */ }
    finally { setCargandoSerie(false); }
  }, [empresaId, sucursalActivaId, diasNum]);
  useEffect(() => { cargarSerie(); }, [cargarSerie]);

  // Totales de la serie
  const tot = useMemo(() => {
    return {
      ventas: serie.reduce((s, d) => s + Number(d.ventas || 0), 0),
      utilidad: serie.reduce((s, d) => s + Number(d.utilidad || 0), 0),
      trans: serie.reduce((s, d) => s + Number(d.transacciones || 0), 0),
    };
  }, [serie]);

  // ── Generadores de PDF / Excel ─────────────────────────────

  const cabecera = (doc, titulo) => {
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(titulo, 14, 18);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text(`${nombreEmpresa} · ${nombreSucursal}`, 14, 25);
    doc.text(`Período: ${fmtFecha(desde)} — ${fmtFecha(hasta)}`, 14, 30);
    doc.text(`Generado: ${new Date().toLocaleString('es-CO')}`, 14, 35);
  };

  const exportarPDF = (titulo, columnas, filas, footer) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    cabecera(doc, titulo);
    autoTable(doc, {
      startY: 42,
      head: [columnas.map(c => c.label)],
      body: filas.map(row => columnas.map(c =>
        c.formato ? c.formato(row[c.key]) : (row[c.key] ?? ''))),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [21, 101, 192], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      margin: { left: 14, right: 14 },
    });
    if (footer) {
      const y = doc.lastAutoTable.finalY + 6;
      doc.setFontSize(10); doc.setFont(undefined, 'bold');
      footer.forEach((line, i) => doc.text(line, 14, y + i * 5));
    }
    doc.save(`${titulo.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_${Date.now()}.pdf`);
  };

  const exportarExcel = (titulo, columnas, filas, footer) => {
    const aoa = [
      [nombreEmpresa],
      [`Sucursal: ${nombreSucursal}`],
      [`Período: ${fmtFecha(desde)} — ${fmtFecha(hasta)}`],
      [`Generado: ${new Date().toLocaleString('es-CO')}`],
      [],
      columnas.map(c => c.label),
      ...filas.map(row => columnas.map(c =>
        // Para Excel usamos valores nativos cuando podemos (números)
        typeof row[c.key] === 'number' ? row[c.key] :
        (c.formato ? c.formato(row[c.key]) : (row[c.key] ?? '')))),
    ];
    if (footer) { aoa.push([]); footer.forEach(line => aoa.push([line])); }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    // Auto width simple: max length por columna
    ws['!cols'] = columnas.map((_, i) => ({
      wch: Math.min(40, Math.max(...aoa.map(r => String(r[i] ?? '').length))),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
    XLSX.writeFile(wb, `${titulo.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_${Date.now()}.xlsx`);
  };

  // ── Handlers por reporte ─────────────────────────────────

  const runExport = async (idReporte, formato) => {
    setExportando(`${idReporte}-${formato}`);
    try {
      await handlers[idReporte](formato);
    } catch (e) {
      notify(e.mensajeUsuario || `Error generando el reporte "${idReporte}".`, 'error');
    } finally { setExportando(null); }
  };

  const handlers = {
    ventas: async (formato) => {
      const cols = [
        { key: 'dia',           label: 'Día' },
        { key: 'ventas',        label: 'Ventas',        formato: fmtCOP },
        { key: 'utilidad',      label: 'Utilidad',      formato: fmtCOP },
        { key: 'transacciones', label: 'Transacciones', formato: fmtNum },
      ];
      const footer = [
        `Total ventas: ${fmtCOP(tot.ventas)}`,
        `Total utilidad: ${fmtCOP(tot.utilidad)}`,
        `Total transacciones: ${fmtNum(tot.trans)}`,
        tot.trans > 0 ? `Ticket promedio: ${fmtCOP(tot.ventas / tot.trans)}` : '',
      ].filter(Boolean);
      const filas = serie.length ? serie : [];
      if (filas.length === 0) { notify('Sin datos en el período.', 'info'); return; }
      (formato === 'pdf' ? exportarPDF : exportarExcel)('Ventas por período', cols, filas, footer);
    },

    productos: async (formato) => {
      const data = await apiReportes.productosMasVendidos(empresaId, sucursalActivaId, desde, hasta, 50);
      if (!data?.length) { notify('Sin productos vendidos en el período.', 'info'); return; }
      const cols = [
        { key: 'codigoProducto',   label: 'Código' },
        { key: 'nombreProducto',   label: 'Producto' },
        { key: 'cantidadVendida',  label: 'Unid. vendidas', formato: fmtNum },
        { key: 'numeroVentas',     label: 'Ventas',         formato: fmtNum },
        { key: 'totalVentas',      label: 'Total facturado', formato: fmtCOP },
      ];
      const totalUnid = data.reduce((s, x) => s + Number(x.cantidadVendida || 0), 0);
      const totalFac  = data.reduce((s, x) => s + Number(x.totalVentas || 0), 0);
      const footer = [
        `Total unidades vendidas: ${fmtNum(totalUnid)}`,
        `Total facturado: ${fmtCOP(totalFac)}`,
      ];
      (formato === 'pdf' ? exportarPDF : exportarExcel)('Productos más vendidos', cols, data, footer);
    },

    inventario: async (formato) => {
      const r = await apiReportes.inventarioEstado(empresaId, sucursalActivaId);
      const data = r?.productos || [];
      if (!data.length) { notify('No hay productos que controlen inventario.', 'info'); return; }
      const cols = [
        { key: 'codigo',        label: 'Código' },
        { key: 'nombre',        label: 'Producto' },
        { key: 'categoria',     label: 'Categoría' },
        { key: 'stockActual',   label: 'Stock',    formato: fmtNum },
        { key: 'stockMinimo',   label: 'Mín.',     formato: fmtNum },
        { key: 'costoUnitario', label: 'Costo',    formato: fmtCOP },
        { key: 'precioVenta',   label: 'Precio',   formato: fmtCOP },
        { key: 'valorizacion',  label: 'Valorización', formato: fmtCOP },
        { key: 'estado',        label: 'Estado' },
      ];
      const footer = [
        `Total productos: ${fmtNum(r.resumen.totalProductos)}`,
        `Sin stock: ${fmtNum(r.resumen.sinStock)}`,
        `Bajo mínimo: ${fmtNum(r.resumen.bajoStock)}`,
        `Valorización total: ${fmtCOP(r.resumen.valorizacionTotal)}`,
      ];
      (formato === 'pdf' ? exportarPDF : exportarExcel)('Estado del inventario', cols, data, footer);
    },

    clientes: async (formato) => {
      const data = await apiReportes.clientesFrecuentes(empresaId, sucursalActivaId, desde, hasta, 50);
      if (!data?.length) {
        notify('Sin clientes con compras en el período (o solo Consumidor Final).', 'info');
        return;
      }
      const cols = [
        { key: 'codigo',         label: 'Código' },
        { key: 'nombre',         label: 'Cliente' },
        { key: 'documento',      label: 'Documento' },
        { key: 'celular',        label: 'Celular' },
        { key: 'numeroCompras',  label: 'Compras',        formato: fmtNum },
        { key: 'total',          label: 'Total facturado', formato: fmtCOP },
        { key: 'ticketPromedio', label: 'Ticket prom.',    formato: fmtCOP },
        { key: 'saldoActual',    label: 'Saldo fiado',     formato: fmtCOP },
        { key: 'ultimaCompra',   label: 'Última compra',   formato: fmtFecha },
      ];
      const totalFac = data.reduce((s, x) => s + Number(x.total || 0), 0);
      const footer = [
        `Total clientes: ${fmtNum(data.length)}`,
        `Facturación total del top: ${fmtCOP(totalFac)}`,
      ];
      (formato === 'pdf' ? exportarPDF : exportarExcel)('Clientes frecuentes', cols, data, footer);
    },

    utilidad: async (formato) => {
      const data = await apiReportes.utilidadPorCategoria(empresaId, sucursalActivaId, desde, hasta);
      if (!data?.length) { notify('Sin ventas categorizadas en el período.', 'info'); return; }
      const cols = [
        { key: 'nombre',           label: 'Categoría' },
        { key: 'cantidadVendida',  label: 'Unidades',  formato: fmtNum },
        { key: 'ingresos',         label: 'Ingresos',  formato: fmtCOP },
        { key: 'costos',           label: 'Costos',    formato: fmtCOP },
        { key: 'utilidad',         label: 'Utilidad',  formato: fmtCOP },
        { key: 'margenPorcentaje', label: 'Margen %',  formato: (v) => `${Number(v).toFixed(1)}%` },
      ];
      const totalIng = data.reduce((s, x) => s + Number(x.ingresos || 0), 0);
      const totalCos = data.reduce((s, x) => s + Number(x.costos || 0), 0);
      const totalUti = totalIng - totalCos;
      const margen = totalIng > 0 ? (totalUti / totalIng) * 100 : 0;
      const footer = [
        `Ingresos totales: ${fmtCOP(totalIng)}`,
        `Costos totales: ${fmtCOP(totalCos)}`,
        `Utilidad total: ${fmtCOP(totalUti)}`,
        `Margen consolidado: ${margen.toFixed(1)}%`,
      ];
      (formato === 'pdf' ? exportarPDF : exportarExcel)('Utilidad y márgenes', cols, data, footer);
    },

    domicilios: async (formato) => {
      const r = await apiReportes.domiciliosResumen(empresaId, sucursalActivaId, desde, hasta);
      const data = r?.pedidos || [];
      if (!data.length) { notify('Sin domicilios en el período.', 'info'); return; }
      const cols = [
        { key: 'numeroPedido',    label: 'Pedido' },
        { key: 'fechaSolicitud',  label: 'Solicitud',    formato: fmtFecha },
        { key: 'fechaEntrega',    label: 'Entrega',      formato: fmtFecha },
        { key: 'estado',          label: 'Estado' },
        { key: 'direccion',       label: 'Dirección' },
        { key: 'valorPedido',     label: 'Valor',        formato: fmtCOP },
        { key: 'tarifaDomicilio', label: 'Domicilio',    formato: fmtCOP },
      ];
      const porEstado = r.resumen.porEstado || [];
      const footer = [
        `Total pedidos: ${fmtNum(r.resumen.totalPedidos)}`,
        `Total facturado: ${fmtCOP(r.resumen.totalFacturado)}`,
        ...porEstado.map(e => `${e.estado}: ${fmtNum(e.cantidad)} pedidos · ${fmtCOP(e.total)}`),
      ];
      (formato === 'pdf' ? exportarPDF : exportarExcel)('Domicilios HolaVeci', cols, data, footer);
    },
  };

  const reportesDisponibles = [
    { id: 'ventas',     titulo: 'Ventas por período',      desc: 'Detalle de ventas y utilidad por rango',      icon: <PaidIcon />,          color: 'primary.main' },
    { id: 'productos',  titulo: 'Productos más vendidos',  desc: 'Ranking por cantidad y facturación',          icon: <Inventory2Icon />,    color: 'success.main' },
    { id: 'inventario', titulo: 'Estado del inventario',   desc: 'Existencias, valorización y stock bajo',      icon: <ReceiptLongIcon />,   color: 'warning.main' },
    { id: 'clientes',   titulo: 'Clientes frecuentes',     desc: 'Top clientes por facturación (excluye CF)',   icon: <PeopleIcon />,        color: 'info.main' },
    { id: 'utilidad',   titulo: 'Utilidad y márgenes',     desc: 'Rentabilidad por categoría',                  icon: <AnalyticsIcon />,     color: 'secondary.main' },
    { id: 'domicilios', titulo: 'Domicilios',              desc: 'Entregas HolaVeci por estado',                icon: <LocalShippingIcon />, color: '#5e35b1' },
  ];

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
             alignItems={{ sm: 'center' }} gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Reportes</Typography>
          <Typography color="text.secondary">Análisis y descarga de informes</Typography>
        </Box>
        <ToggleButtonGroup value={periodo} exclusive
                           onChange={(_, v) => v && setPeriodo(v)}
                           size="small" color="primary">
          <ToggleButton value="7">7 días</ToggleButton>
          <ToggleButton value="15">15 días</ToggleButton>
          <ToggleButton value="30">30 días</ToggleButton>
          <ToggleButton value="90">90 días</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Evolución de ventas y utilidad</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" variant="outlined"
                    label={`${serie.length} días · ${fmtNum(tot.trans)} ventas · ${fmtCOP(tot.ventas)}`}
                    color={tot.ventas > 0 ? 'success' : 'default'} />
              <Button size="small" onClick={() => {
                console.log('[Reportes] Datos de serieDiaria desde backend:', serie);
                notify(`Backend devolvió ${serie.length} días. Total ventas: ${fmtCOP(tot.ventas)}. Detalle en consola (F12).`, 'info');
              }}>
                Ver datos del backend
              </Button>
            </Stack>
          </Stack>
          {cargandoSerie ? (
            <LinearProgress />
          ) : serie.length === 0 ? (
            <Alert severity="info">
              El backend no devolvió datos. Verifica que hay ventas en pos.Ventas para el período.
            </Alert>
          ) : tot.ventas === 0 && tot.trans === 0 ? (
            <Alert severity="info">
              El backend devolvió {serie.length} días pero ninguno con ventas registradas.
              El chart está plano en $0. Si ves una curva con valores, es cache del navegador
              (Ctrl+Shift+R para forzar recarga).
            </Alert>
          ) : (
            <Box sx={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <AreaChart data={serie}>
                  <defs>
                    <linearGradient id="colVentas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0F4C3A" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#0F4C3A" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colUtil" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#2e7d32" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }}
                         tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => fmtCOP(v)} />
                  <Legend />
                  <Area type="monotone" dataKey="ventas" stroke="#0F4C3A"
                        fill="url(#colVentas)" strokeWidth={2.5} name="Ventas" />
                  <Area type="monotone" dataKey="utilidad" stroke="#2e7d32"
                        fill="url(#colUtil)" strokeWidth={2.5} name="Utilidad" />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          )}
        </CardContent>
      </Card>

      <Typography variant="h6" mb={2}>Reportes disponibles</Typography>
      <Grid container spacing={2.5}>
        {reportesDisponibles.map((r) => {
          const busyPdf = exportando === `${r.id}-pdf`;
          const busyXls = exportando === `${r.id}-xlsx`;
          return (
            <Grid item xs={12} sm={6} md={4} key={r.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="flex-start" mb={2}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: 2, bgcolor: r.color, opacity: 0.15,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {React.cloneElement(r.icon, { sx: { color: r.color } })}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>{r.titulo}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.desc}</Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button fullWidth size="small" variant="outlined"
                            startIcon={busyPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                            disabled={busyPdf || busyXls}
                            onClick={() => runExport(r.id, 'pdf')}>
                      PDF
                    </Button>
                    <Button fullWidth size="small" variant="outlined"
                            startIcon={busyXls ? <CircularProgress size={16} /> : <TableChartIcon />}
                            disabled={busyPdf || busyXls}
                            onClick={() => runExport(r.id, 'xlsx')}>
                      Excel
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default Reportes;
